import { ChangeType, ContentDiffEntry, ContentDiffPayload } from './contentDiffTypes';

const IGNORED_PROPERTY_SEGMENTS = [
  'tone',
  'editMode',
  'printMode',
  'modelType',
  'modelId',
  'preventImageCrop',
  'removeable',
  'focusElement',
  'selectFocusedElement',
  'onModifyActionButtons',
  'onUpdate',
  'onAdd',
  'onMove',
  'onRemove',
  'firstElement',
  'lastElement',
  'triggerAdd',
  'showEditButtons',
];

const propertySegmentRegexes = IGNORED_PROPERTY_SEGMENTS.map(
  (segment) => new RegExp(`(?:\\.|\\[)${segment}(?:\\.|\\[|$)`),
);

export const getOrdinal = (num: number): string => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const remainder = num % 100;
  return num + (suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0]);
};

export const makePathHumanReadable = (path: string): string => {
  if (!path) return path;

  let humanPath = path
    .replace(/\[(\d+)\]/g, (match, index) => {
      const num = parseInt(index, 10);
      const position = num + 1;
      const ordinal = getOrdinal(position);
      return ` in ${ordinal} position`;
    })
    .replace(/\.properties\./g, ' ')
    .replace(/\./g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const propertyMappings: Record<string, string> = {
    heading: 'header',
    subHeader: 'sub-header',
    content: 'content',
    text: 'text',
    title: 'title',
    description: 'description',
    items: 'item',
    rows: 'row',
    cells: 'cell',
    footnotes: 'footnote',
  };

  Object.entries(propertyMappings).forEach(([key, value]) => {
    const regex = new RegExp(`\\b${key}\\b`, 'g');
    humanPath = humanPath.replace(regex, value);
  });

  humanPath = humanPath
    .replace(/\s+/g, ' ')
    .replace(/\b(\w+)\s+(\w+)\s+(\w+)\b/g, '$1 $2 $3')
    .trim();

  return humanPath.charAt(0).toUpperCase() + humanPath.slice(1);
};

export const shouldFilterDiffEntry = (entry: ContentDiffEntry): boolean => {
  const path = entry.path ?? '';

  const beforeValue = entry.before;
  const afterValue = entry.after;
  const isBeforeEmpty = beforeValue === null || beforeValue === undefined || beforeValue === '';
  const isAfterEmpty = afterValue === null || afterValue === undefined || afterValue === '';

  if (isBeforeEmpty && isAfterEmpty) {
    return true;
  }

  if (!path.includes('.properties.')) {
    return false;
  }

  if (/\.on[A-Z]/.test(path)) {
    return true;
  }

  return propertySegmentRegexes.some((regex) => regex.test(path));
};

export const cleanDiffPayload = (payload: ContentDiffPayload): ContentDiffPayload => {
  const entries = payload.entries || [];
  const filteredEntries = entries.filter((entry) => !shouldFilterDiffEntry(entry));

  if (entries.length === filteredEntries.length) {
    return payload;
  }

  if (!payload.summary) {
    return { ...payload, entries: filteredEntries };
  }

  const counts = filteredEntries.reduce<Record<ChangeType, number>>(
    (acc, entry) => {
      acc[entry.changeType] += 1;
      return acc;
    },
    { added: 0, removed: 0, changed: 0 },
  );

  return {
    ...payload,
    entries: filteredEntries,
    summary: {
      ...payload.summary,
      added: counts.added,
      removed: counts.removed,
      changed: counts.changed,
      total: filteredEntries.length,
    },
  };
};

export const extractContentDiffPayload = (value: any): ContentDiffPayload | null => {
  if (!value) {
    return null;
  }

  let payload: ContentDiffPayload | null = null;

  if (Array.isArray(value.entries)) {
    payload = value as ContentDiffPayload;
  } else if (value.diff && Array.isArray(value.diff.entries)) {
    payload = value.diff as ContentDiffPayload;
  } else if (value.payload && Array.isArray(value.payload.entries)) {
    payload = value.payload as ContentDiffPayload;
  }

  if (!payload) {
    return null;
  }

  return cleanDiffPayload(payload);
};
